import { Component, OnInit } from '@angular/core';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BsDropdownDirective, BsDropdownMenuDirective, BsDropdownToggleDirective } from 'ngx-bootstrap/dropdown';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmModalComponent } from '../../shared/modals/confirm-modal/confirm-modal.component';
import { MerchantsService } from '../../core/services/merchants.service';
import { ToastNotificationsService } from '../../core/services/toast-notifications.service';
import { LoaderComponent } from '../../shared-with-client/loader/loader.component';

const ALLOWED_FILE_URL_HOSTS = new Set([
  'firebasestorage.googleapis.com',
  'storage.googleapis.com',
  'image-uploader-service.appspot.com',
]);

@Component({
  selector: 'app-document-preview-modal',
  templateUrl: './document-preview-modal.component.html',
  styleUrls: ['./document-preview-modal.component.scss'],
  imports: [
    TranslatePipe,
    LoaderComponent,
    BsDropdownDirective,
    BsDropdownToggleDirective,
    BsDropdownMenuDirective
],
  standalone: true,
})
export class DocumentPreviewModalComponent implements OnInit {
  public attachment: any;
  public client: any;
  public deleted = false;
  public loading = true;
  public visualizer: 'pdf' | 'image' | 'other' = 'other';
  public fileUrl: SafeResourceUrl | string | undefined;
  public canDelete = true;

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private sanitizer: DomSanitizer,
    private merchantsService: MerchantsService,
    private toastNotificationsService: ToastNotificationsService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    const fileType = String(this.attachment?.fileType || '').toLowerCase();
    const fileName = String(this.attachment?.fileName || '');
    const rawFileUrl = String(this.attachment?.fileUrl || '');
    const canPreviewUrl = this.isAllowedFileUrl(rawFileUrl);

    if (canPreviewUrl && (fileType === 'application/pdf' || /\.pdf$/i.test(fileName))) {
      this.visualizer = 'pdf';
      const previewUrl = new URL(rawFileUrl);
      previewUrl.hash = 'toolbar=0';
      this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl.toString());
    } else if (canPreviewUrl && (fileType.startsWith('image/') || /\.(jpe?g|png|gif|webp|svg|bmp|tiff)$/i.test(fileName))) {
      this.visualizer = 'image';
      this.fileUrl = rawFileUrl;
      this.loading = false;
    } else {
      this.visualizer = 'other';
      this.loading = false;
    }
  }

  onPdfLoaded() {
    this.loading = false;
  }

  downloadAttachment() {
    const fileUrl = this.attachment?.fileUrl;
    if (!this.isAllowedFileUrl(fileUrl)) {
      return;
    }

    const link = document.createElement('a');
    link.href = fileUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = this.attachment?.fileName || 'attachment';
    link.click();
  }

  deleteAttachment() {
    if (!this.canDelete) {
      return;
    }

    const initialState = {
      question: this.translate.instant('DASHBOARD.documentPreviewModal.deleteAttachmentTitle'),
    };
    this.modalService.show(ConfirmModalComponent, { initialState, class: 'second' });
    const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
      modalCallback.unsubscribe();
      if (reason && typeof reason === 'boolean') {
        this.merchantsService.deleteClientFile(this.client.id, this.attachment.id).subscribe({
          next: () => {
            this.deleted = true;
            this.modalService.setDismissReason('deleted');
            this.bsModalRef.hide();
          },
          error: (err) => {
            this.toastNotificationsService.showToast(
              err?.errMsg || this.translate.instant('GENERAL.generalErrorMsg'),
              'error',
            );
          },
        });
      }
    });
  }

  close() {
    this.bsModalRef.hide();
  }

  private isAllowedFileUrl(fileUrl: string | undefined): boolean {
    if (!fileUrl) {
      return false;
    }

    try {
      const parsed = new URL(fileUrl);
      return parsed.protocol === 'https:'
        && !parsed.username
        && !parsed.password
        && ALLOWED_FILE_URL_HOSTS.has(parsed.hostname.toLowerCase());
    } catch {
      return false;
    }
  }
}
